import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  useColorModeValue,
  IconButton,
  Image,
  Center,
  Badge
} from '@chakra-ui/react';
import { FiArrowLeft, FiPlus, FiPlay, FiEdit, FiTrash2 } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { getContent } from '../services/contentService';

interface Season {
  id: string;
  title: string;
  seasonNumber: string;
  poster?: string;
  episodeCount: number;
  description?: string;
}

const SeasonsManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { seriesId } = useParams<{ seriesId: string }>();
  const toast = useToast();
  
  const [series, setSeries] = useState<any>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const loadSeries = useCallback(async () => {
    try {
      setLoading(true);
      const seriesData = await getContent('series', 1);
      const foundSeries = seriesData.find(s => s.id === seriesId);
      
      if (foundSeries) {
        setSeries(foundSeries);
        // Load seasons from localStorage or create default Season 1
        const savedSeasons = localStorage.getItem(`seasons-${seriesId}`);
        if (savedSeasons) {
          setSeasons(JSON.parse(savedSeasons));
        } else {
          // Create default Season 1 if no seasons exist
          const defaultSeason: Season = {
            id: 'season-1',
            title: `${foundSeries.title} - Season 1`,
            seasonNumber: '1',
            poster: foundSeries.poster,
            episodeCount: 0,
            description: foundSeries.description
          };
          setSeasons([defaultSeason]);
          localStorage.setItem(`seasons-${seriesId}`, JSON.stringify([defaultSeason]));
        }
      }
    } catch (error) {
      console.error('Error loading series:', error);
      toast({
        title: 'Error',
        description: 'Failed to load series information.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [seriesId, toast]);

  useEffect(() => {
    if (seriesId) {
      loadSeries();
    }
  }, [seriesId, loadSeries]);

  const handleCreateSeason = () => {
    // Navigate to create season page
    navigate(`/series/${seriesId}/create-season`);
  };

  const handleManageEpisodes = (seasonId: string) => {
    // Navigate to episodes management for this season
    navigate(`/series/${seriesId}/season/${seasonId}/episodes`);
  };

  const handleEditSeason = (seasonId: string) => {
    // Navigate to season management page
    navigate(`/series/${seriesId}/season/${seasonId}/manage`);
  };

  const handleDeleteSeason = (seasonId: string, seasonTitle: string) => {
    if (window.confirm(`Are you sure you want to delete "${seasonTitle}"? This will also delete all episodes in this season. This action cannot be undone.`)) {
      // Delete season from localStorage
      const seasonsKey = `seasons-${seriesId}`;
      const existingSeasons = JSON.parse(localStorage.getItem(seasonsKey) || '[]');
      const updatedSeasons = existingSeasons.filter((season: any) => season.id !== seasonId);
      localStorage.setItem(seasonsKey, JSON.stringify(updatedSeasons));
      
      // Also delete all episodes for this season
      const episodesKey = `episodes-${seriesId}`;
      const existingEpisodes = JSON.parse(localStorage.getItem(episodesKey) || '[]');
      const updatedEpisodes = existingEpisodes.filter((ep: any) => ep.seasonId !== seasonId);
      localStorage.setItem(episodesKey, JSON.stringify(updatedEpisodes));
      
      // Refresh seasons list
      refreshSeasons();
      
      toast({
        title: 'Season deleted',
        description: 'Season and all its episodes have been deleted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  };


  // Function to add a new season (called from CreateSeason component)
  const addSeason = useCallback((seasonNumber: string) => {
    const newSeason: Season = {
      id: `season-${Date.now()}`, // Simple ID generation
      title: `${series?.title} - Season ${seasonNumber}`,
      seasonNumber: seasonNumber,
      poster: series?.poster,
      episodeCount: 0, // Start with 0 episodes
      description: series?.description
    };
    
    setSeasons(prev => {
      const updatedSeasons = [...prev, newSeason];
      // Save to localStorage
      localStorage.setItem(`seasons-${seriesId}`, JSON.stringify(updatedSeasons));
      return updatedSeasons;
    });
  }, [series, seriesId]);

  // Function to refresh seasons data (called when episodes are added)
  const refreshSeasons = useCallback(() => {
    const savedSeasons = localStorage.getItem(`seasons-${seriesId}`);
    if (savedSeasons) {
      const seasonsData = JSON.parse(savedSeasons);
      // Update episode counts based on actual episodes
      const episodes = JSON.parse(localStorage.getItem(`episodes-${seriesId}`) || '[]');
      const updatedSeasons = seasonsData.map((season: any) => {
        const seasonEpisodes = episodes.filter((ep: any) => ep.seasonId === season.id);
        return { ...season, episodeCount: seasonEpisodes.length };
      });
      setSeasons(updatedSeasons);
      localStorage.setItem(`seasons-${seriesId}`, JSON.stringify(updatedSeasons));
    }
  }, [seriesId]);

  // Expose functions globally
  React.useEffect(() => {
    (window as any).addSeasonToSeries = addSeason;
    (window as any).refreshSeasons = refreshSeasons;
    return () => {
      delete (window as any).addSeasonToSeries;
      delete (window as any).refreshSeasons;
    };
  }, [addSeason, refreshSeasons]);

  // Also set them immediately when component mounts
  React.useEffect(() => {
    (window as any).addSeasonToSeries = addSeason;
    (window as any).refreshSeasons = refreshSeasons;
  }, [addSeason, refreshSeasons]);

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to manage seasons.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  if (loading) {
    return (
      <Center h="50vh">
        <Text>Loading...</Text>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={4} borderBottom="1px solid" borderColor="gray.200">
        <HStack gap={4} align="center">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Seasons
          </Text>
          <Text fontSize="sm" color="gray.500" ml="auto">
            {seasons.length}/5
          </Text>
        </HStack>
      </Box>

      {/* Series Title */}
      {series && (
        <Box p={4}>
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            {series.title} – Season 1
          </Text>
        </Box>
      )}

      {/* Main Content */}
      <Box p={6}>
        <VStack spacing={4} align="stretch">
          {/* Existing Seasons */}
          {seasons.map((season) => (
            <Box
              key={season.id}
              bg={cardBg}
              borderRadius="lg"
              p={4}
              border="1px solid"
              borderColor="gray.200"
            >
              <HStack gap={4} align="start">
                {/* Season Poster */}
                <Box flex="0 0 120px">
                  <Image
                    src={season.poster || '/placeholder-poster.jpg'}
                    alt={season.title}
                    w="120px"
                    h="160px"
                    objectFit="cover"
                    borderRadius="md"
                    fallback={
                      <Box
                        w="120px"
                        h="160px"
                        bg="gray.200"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <IconWrapper icon={FiPlay} size={32} color="gray.400" />
                      </Box>
                    }
                  />
                </Box>

                {/* Season Details */}
                <VStack align="start" flex="1" gap={2}>
                  <HStack w="full" justify="space-between">
                    <Text fontSize="lg" fontWeight="bold" color={textColor}>
                      {season.title}
                    </Text>
                    <HStack gap={1}>
                      <IconButton
                        aria-label="Edit season"
                        icon={<IconWrapper icon={FiEdit} size={16} />}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditSeason(season.id)}
                        colorScheme="blue"
                      />
                      <IconButton
                        aria-label="Delete season"
                        icon={<IconWrapper icon={FiTrash2} size={16} />}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSeason(season.id, season.title)}
                        colorScheme="red"
                      />
                    </HStack>
                  </HStack>
                  
                  <Text fontSize="sm" color={labelColor}>
                    Add Episodes
                  </Text>
                  
                  <HStack gap={2}>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      variant="outline"
                      onClick={() => handleManageEpisodes(season.id)}
                    >
                      Manage Episodes
                    </Button>
                    <Badge colorScheme="blue" variant="subtle">
                      {season.episodeCount} episodes
                    </Badge>
                  </HStack>
                </VStack>
              </HStack>
            </Box>
          ))}

          {/* Create Season Button */}
          <Box
            bg="white"
            borderRadius="lg"
            p={8}
            border="2px dashed"
            borderColor="gray.300"
            textAlign="center"
            cursor="pointer"
            _hover={{ borderColor: 'purple.400', bg: 'purple.50' }}
            onClick={handleCreateSeason}
          >
            <VStack gap={3}>
              <IconWrapper icon={FiPlus} size={32} color="gray.400" />
              <Text fontSize="lg" fontWeight="medium" color="gray.600">
                Create Season
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default SeasonsManagement;
